import AdminApp from "./AdminApp";
import AttachmentsApp from "./AttachmentsApp";
import FormApp from "./FormApp";
import ProfilerApp from "./ProfilerApp";
import ProgramApp from "./ProgramApp";
import ReportApp from "./ReportApp";
import ReviewApp from "./ReviewApp";
import ScenariosApp from "./ScenariosApp";
import SettingsApp from "./SettingsApp";
import SubmissionApp from "./SubmissionApp";
import TemplateRequestsApp from "./TemplateRequestsApp";
import TemplatesApp from "./TemplatesApp";
import TokenApp from "./TokenApp";
import WarnOnPageExit from "./WarnOnPageExit";
import AppBar from "./common/AppBar";
import ProfilerBanner from "./common/ProfilerBanner";
import { globalTitleFor, ownsFavicon } from "./globalTitle";
import ReportsApp from "./reportsV3/ReportsApp";
import TriagePipeline from "./triagePipeline/TriagePipeline";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Tracker from "@openreplay/tracker";
import trackerAssist from "@openreplay/tracker-assist";
import * as Sentry from "@sentry/react";
import MaintenanceBlocker from "components/maintenance/MaintenanceBlocker";
import * as config from "config";
import { SUBMISSION_TRIAGE } from "flags";
import { SnackbarProvider } from "notistack";
import { useEffect } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { connect } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useConfigQuery } from "services/staticDataService";
import * as featureFlagHooks from "store/hooks/featureFlagHooks";
import * as trackerHooks from "store/hooks/trackerHooks";
import * as userSelectors from "store/selectors/user/userSelectors";
import * as utils from "utils";

const tracker =
  config.getOpenReplayProjectId() != null
    ? new Tracker({
        projectKey: config.getOpenReplayProjectId(),
        __DISABLE_SECURE_MODE: config.getOpenReplayDisableSecureMode(),
      })
    : null;
tracker?.use(trackerAssist({}));
tracker?.start({
  metadata: {
    environment:
      process.env.REACT_APP_SENTRY_ENVIRONMENT || "UNKNOWN_ENVIRONMENT",
  },
});

const RootApp = ({ email, companyId, isSuperUser, triageEnabled }) => {
  trackerHooks.useHeap();
  const location = useLocation();
  const { data: generalConfiguration } = useConfigQuery(
    "general_configuration"
  );
  const workspaceName = generalConfiguration?.workspace?.name;

  useEffect(() => {
    Sentry.setUser({ email });
    tracker?.setUserID(email);
    tracker?.setMetadata("companyId", companyId);
  }, [email, companyId]);

  useEffect(() => {
    const title = globalTitleFor(location.pathname, workspaceName);
    if (title !== null) {
      document.title = title;
    }
    if (!ownsFavicon(location.pathname)) {
      utils.setFavicon(`/favicon.png?v=${Date.now()}`, "image/png");
    }
  }, [location.pathname, workspaceName]);

  featureFlagHooks.useLocalStorageFeatureFlags();

  return (
    // One drag-and-drop provider for the whole application. react-dnd keeps a
    // single HTML5 backend per window and throws "Cannot have two HTML5
    // backends at the same time" if a second one is set up before the first
    // has torn down. Mounting it once here, for the lifetime of the app,
    // means a screen that unmounts and remounts a draggable area can never
    // trigger that. Individual screens must not mount their own.
    <DndProvider backend={HTML5Backend}>
      <MaintenanceBlocker>
        <SnackbarProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <ProfilerBanner />
            <AppBar />
            <WarnOnPageExit>
              <Routes>
                <Route path={"/token/*"} element={<TokenApp />} />
                <Route path={"/profiler/*"} element={<ProfilerApp />} />
                <Route
                  path={"/transfers/*"}
                  element={<SubmissionApp source={"transfers"} />}
                />
                <Route path={"/submissions/*"} element={<SubmissionApp />} />
                <Route path={"/forms/*"} element={<FormApp />} />
                <Route path={"/programs/*"} element={<ProgramApp />} />
                <Route path={"/reports/*"} element={<ReportApp />} />
                <Route path={"/report-builder/*"} element={<ReportsApp />} />
                <Route path={"/review/*"} element={<ReviewApp />} />
                <Route path={"/scenarios/*"} element={<ScenariosApp />} />
                <Route path={"/attachments/*"} element={<AttachmentsApp />} />
                <Route path={"/settings/*"} element={<SettingsApp />} />
                <Route
                  path={"/template-requests/*"}
                  element={<TemplateRequestsApp />}
                />
                <Route path={"/templates/*"} element={<TemplatesApp />} />
                {triageEnabled && (
                  <Route
                    path={"/triage-pipeline/*"}
                    element={<TriagePipeline />}
                  />
                )}
                <Route path={"/admin/*"} element={<AdminApp />} />
                {isSuperUser ? (
                  <Route path={"*"} element={<Navigate to={"/admin"} />} />
                ) : (
                  <Route
                    path={"*"}
                    element={<Navigate to={"/submissions"} />}
                  />
                )}
              </Routes>
            </WarnOnPageExit>
          </LocalizationProvider>
        </SnackbarProvider>
      </MaintenanceBlocker>
    </DndProvider>
  );
};

const mapStateToProps = (state) => {
  const userAttributes = userSelectors.selectUserAttributes(state);
  const userConfig = userSelectors.selectUserConfig(state);
  return {
    email: userAttributes?.email || "UNKNOWN_USER",
    companyId: userAttributes?.["custom:company_id"] || "UNKNOWN_COMPANY",
    isSuperUser: userSelectors.isSuperUser(state),
    triageEnabled: userConfig.componentVisibility?.[SUBMISSION_TRIAGE] ?? false,
  };
};

export default connect(mapStateToProps)(RootApp);
